/**
 * Scanner DataTable: preset column widths (keep in sync with buildDefaultColumns in DataTable.tsx)
 * and content-based width hints so ticker / text columns shrink to data by default.
 */
import type { ScannerData } from '../types/ScannerData';
import { getExpiryFromRecord } from './filterUtils';

/** Legacy width used when a column key is missing from the preset map. */
export const COLUMN_WIDTH_CATALOG_DEFAULT = 120;

/**
 * Default `ColumnConfig.width` per key — must match `buildDefaultColumns` in DataTable.tsx.
 */
/** Bracket keys only — avoids parser ambiguity on keys with spaces (e.g. Delta accumelated). */
export const SCANNER_COLUMN_PRESET_WIDTH: Record<string, number> = {
  ['Underlying']: 120,
  ['Option Type']: 80,
  ['Strike']: 100,
  ['Expiry Date']: 120,
  ['Days left']: 100,
  ['Last']: 100,
  ['Delta ( ask - bid )']: 140,
  ['Delta accumelated']: 120,
  ['Delta Premium']: 120,
  ['Delta Accumulated Premium']: 160,
  ['Zahn (Count Indicator)']: 120,
  ['Symbol']: 100,
  ['Bid Count']: 100,
  ['Ask Count']: 100,
  ['Trade Count']: 100,
  ['Bid Volume']: 120,
  ['Ask Volume']: 120,
  ['Direction']: 100,
  ['Sentiment']: 100,
  ['Moneyness']: 100,
  ['Vol/OI']: 100,
  ['Mid']: 100,
  ['Volume']: 100,
  ['Volume D-1']: 100,
  ['Volume D-2']: 100,
  ['Volume D-3']: 100,
  ['Volume D-4']: 100,
  ['Open Interest']: 120,
  ['Implied Volatility']: 140,
  ['Underlying Price']: 120,
  ['Bid']: 100,
  ['Ask']: 100,
  ['Total Volume']: 120,
  ['Sentiment accumelated']: 140,
  ['Direction accumelated']: 140,
  ['Gamma']: 100,
  ['Bid Volume Premium']: 140,
  ['Ask Volume Premium']: 140,
  ['Bid Accumulated Premium']: 160,
  ['Ask Accumulated Premium']: 160,
  ['Haltz (Volume Concentration)']: 140,
  ['Band (IV Indicator)']: 120,
  ['Mal (Gamma Indicator)']: 120,
  ['Rho']: 100,
  ['Lambda']: 100,
  ['Theoretical Value']: 120,
  ['Overall Premium']: 140,
};

function formatDaysLeftPlain(item: ScannerData): string {
  const formatDaysLeftLabel = (daysLeft: number) => {
    if (daysLeft < 0) return 'Expired';
    if (daysLeft === 0) return 'Today';
    if (daysLeft === 1) return '1 day';
    return `${daysLeft} days`;
  };
  const expiryStr = getExpiryFromRecord(item) || item['Expiry Date'];
  if (expiryStr) {
    try {
      const expiry = new Date(expiryStr as string);
      const today = new Date();
      const expMs = expiry.getTime();
      const todayMs = today.getTime();
      if (Number.isFinite(expMs) && Number.isFinite(todayMs)) {
        const daysLeft = Math.ceil((expMs - todayMs) / (1000 * 60 * 60 * 24));
        if (Number.isFinite(daysLeft)) return formatDaysLeftLabel(daysLeft);
      }
    } catch {
      /* fall through */
    }
  }
  const raw = item['Days left'];
  if (typeof raw === 'number' && Number.isFinite(raw)) {
    return formatDaysLeftLabel(Math.round(raw));
  }
  const parsed = parseFloat(String(raw ?? '').replace(/,/g, ''));
  if (Number.isFinite(parsed)) return formatDaysLeftLabel(Math.round(parsed));
  return '-';
}

/** Plain string length estimate aligned with DataTable `getCellValue` (for width fitting only). */
export function plainTextForColumnMeasure(item: ScannerData, key: string): string {
  if (key === 'Days left') return formatDaysLeftPlain(item);

  const value = item[key as keyof ScannerData];
  if (value === null || value === undefined) return '-';

  if (typeof value === 'number') {
    if (key.includes('Premium')) {
      return `$${value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    }
    if (key.includes('Haltz') || key.includes('Band') || key.includes('Mal') || key.includes('Zahn')) {
      const percentage = value.toFixed(1);
      const sign = value > 0 ? '+' : '';
      return `${sign}${percentage}%`;
    }
    if (key.includes('Delta') || key.includes('Gamma') || key.includes('Theta') || key.includes('Vega')) {
      return value.toFixed(4);
    }
    if (key.includes('Volume') || key.includes('Count')) {
      return value.toLocaleString();
    }
    if (key.includes('Price') || key.includes('Last') || key.includes('Strike')) {
      return value.toFixed(2);
    }
  }

  return String(value);
}

const SAMPLE_ROWS = 280;
const CHAR_PX = 6.65;
const PAD_PX = 40;
const MIN_COL_PX = 52;
const MAX_BY_KEY: Record<string, number> = {
  Underlying: 240,
  'Option Type': 96,
  Strike: 110,
  'Expiry Date': 220,
  'Days left': 120,
  Last: 120,
};

export interface VisibleColLite {
  key: string;
  label: string;
  width?: number;
}

/**
 * For columns still at their catalog preset width, estimate px from header + sample cell text.
 * Skips columns the user has resized (width differs from preset).
 */
export function estimateScannerColumnContentWidths(
  visible: VisibleColLite[],
  rows: ScannerData[],
): Record<string, number> {
  const out: Record<string, number> = {};
  const sample = rows.length > 0 ? rows.slice(0, SAMPLE_ROWS) : [];

  for (const col of visible) {
    const preset = SCANNER_COLUMN_PRESET_WIDTH[col.key] ?? COLUMN_WIDTH_CATALOG_DEFAULT;
    const stored = Number(col.width);
    if (Number.isFinite(stored) && stored > 0 && stored !== preset) {
      continue;
    }

    let maxChars = Math.max(col.label.length, 4);
    for (const item of sample) {
      const t = plainTextForColumnMeasure(item, col.key);
      maxChars = Math.max(maxChars, t.length);
    }

    const cap = MAX_BY_KEY[col.key] ?? 300;
    const px = Math.round(Math.min(cap, Math.max(MIN_COL_PX, maxChars * CHAR_PX + PAD_PX)));
    out[col.key] = px;
  }

  return out;
}
